import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Typography,
  message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  usePostBundleDealsMutation,
  usePutBundleDealsMutation,
  useValidateBundleDealMutation,
} from "../../../api/admin/webshop/bundles/BundleDealsApi";
import {
  BundleDiscountType,
  BundleItem,
  BundleStatus,
  BundleTier,
  CreateBundleRequest,
  UpdateBundleRequest,
} from "../../../api/admin/webshop/bundles/BundleItem";
import { useGetWsProductsQuery } from "../../../api/webshop/wsProductsApi";
import {
  sgtDayjsToUtcIso,
  utcIsoToSgtDayjs,
} from "../promotions/promoDateUtils";
import { Dayjs } from "dayjs";

const { Text } = Typography;

export type CreateBundleFormProps = {
  showModal: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  mode: "create" | "view" | "edit";
  initialValues?: BundleItem;
};

type FormTier = {
  minQty: number;
  discountType: BundleDiscountType;
  discountValue: number;
};

function mapTiersFromApi(tiers: BundleItem["tiers"] = []): FormTier[] {
  return (tiers || []).map((t: any) => ({
    minQty: t.minQty ?? t.qty ?? t.buyQty ?? 1,
    discountType: (t.discount?.type || "PERCENTAGE") as BundleDiscountType,
    discountValue:
      t.discount?.value ?? t.percent ?? t.getPercentage ?? t.percentage ?? 0,
  }));
}

function mapTiersToApi(tiers: FormTier[] = []): BundleTier[] {
  return tiers.map((t) => ({
    minQty: Number(t.minQty),
    discount: {
      type: t.discountType,
      value: Number(t.discountValue),
    },
  }));
}

export default function CreateBundleForm(props: CreateBundleFormProps) {
  const [form] = Form.useForm();
  const [internalMode, setInternalMode] = useState(props.mode);
  const [noExpiry, setNoExpiry] = useState(false);
  const [noStartDate, setNoStartDate] = useState(false);

  const { data: productsData } = useGetWsProductsQuery(undefined, {
    skip: !props.showModal,
  });
  const [postBundleDeal, { isLoading: isCreating }] =
    usePostBundleDealsMutation();
  const [putBundleDeal, { isLoading: isUpdating }] =
    usePutBundleDealsMutation();
  const [validateBundleDeal, { isLoading: isValidating }] =
    useValidateBundleDealMutation();

  const isView = internalMode === "view";
  const isCreate = internalMode === "create";
  const isSaving = isCreating || isUpdating || isValidating;

  const productOptions = useMemo(
    () =>
      (productsData || []).map((p) => ({
        label: `${p.productTitle || p.title || p.productName || p.productId} (${p.productId})`,
        value: p.productId,
      })),
    [productsData],
  );

  useEffect(() => {
    setInternalMode(props.mode);
  }, [props.mode]);

  useEffect(() => {
    if (!props.showModal) return;

    if (internalMode === "create") {
      form.resetFields();
      setNoExpiry(false);
      setNoStartDate(false);
      form.setFieldsValue({
        promoCode: "",
        name: "",
        description: "",
        status: "ACTIVE",
        startAt: null,
        endAt: null,
        productSelection: "all",
        productIds: [],
        simplyPoints: true,
        otherPromoCode: false,
        membershipBenefits: false,
        tiers: [
          { minQty: 2, discountType: "PERCENTAGE", discountValue: 10 },
        ],
      });
      return;
    }

    if (!props.initialValues) return;

    const item = props.initialValues;
    const appliesTo =
      item.allowedProducts && "appliesTo" in item.allowedProducts
        ? item.allowedProducts.appliesTo
        : "ALL";
    const productIds =
      item.allowedProducts && "ids" in item.allowedProducts
        ? item.allowedProducts.ids || []
        : [];
    const hasNoStart = !item.startAt;
    const hasNoExpiry = !item.endAt;

    setNoStartDate(hasNoStart);
    setNoExpiry(hasNoExpiry);

    form.setFieldsValue({
      promoCode: item.promoCode,
      name: item.name,
      description: item.description || "",
      status: item.status || "ACTIVE",
      startAt: utcIsoToSgtDayjs(item.startAt),
      endAt: utcIsoToSgtDayjs(item.endAt),
      productSelection: appliesTo === "SPECIFIC" ? "selected" : "all",
      productIds,
      simplyPoints: item.combinations?.simplyPoints ?? true,
      otherPromoCode: item.combinations?.otherPromoCode ?? false,
      membershipBenefits: item.combinations?.membershipBenefits ?? false,
      tiers: mapTiersFromApi(item.tiers),
    });
  }, [props.showModal, props.initialValues, internalMode, form]);

  const buildPayload = (values: any): CreateBundleRequest => {
    const startAt = noStartDate ? null : sgtDayjsToUtcIso(values.startAt as Dayjs);
    const endAt = noExpiry ? null : sgtDayjsToUtcIso(values.endAt as Dayjs);
    const productSelection = values.productSelection || "all";
    const productIds: string[] = values.productIds || [];

    return {
      promoCode: String(values.promoCode || "")
        .trim()
        .toUpperCase(),
      name: values.name?.trim() || undefined,
      description: values.description?.trim() || "",
      status: (values.status || "ACTIVE") as BundleStatus,
      tiers: mapTiersToApi(values.tiers),
      allowedProducts:
        productSelection === "selected"
          ? { appliesTo: "SPECIFIC", ids: productIds }
          : { appliesTo: "ALL" },
      combinations: {
        simplyPoints: Boolean(values.simplyPoints),
        otherPromoCode: Boolean(values.otherPromoCode),
        membershipBenefits: Boolean(values.membershipBenefits),
      },
      eligibility: {},
      startAt,
      endAt,
      createdBy: "admin",
    };
  };

  const onFinish = async (values: any) => {
    const payload = buildPayload(values);

    if (!payload.promoCode) {
      message.error("Promo code is required");
      return;
    }
    if (!payload.tiers.length) {
      message.error("Add at least one tier");
      return;
    }

    const minQtys = payload.tiers.map((t) => t.minQty);
    const uniqueSorted = [...minQtys].sort((a, b) => a - b);
    if (new Set(minQtys).size !== minQtys.length) {
      message.error("Each tier must have a unique minimum quantity");
      return;
    }
    if (minQtys.some((qty, i) => qty !== uniqueSorted[i])) {
      message.error("Tiers must have ascending minQty values");
      return;
    }

    if (
      payload.allowedProducts &&
      "appliesTo" in payload.allowedProducts &&
      payload.allowedProducts.appliesTo === "SPECIFIC" &&
      (!payload.allowedProducts.ids || payload.allowedProducts.ids.length === 0)
    ) {
      message.error("Select at least one product when applying to specific products");
      return;
    }

    try {
      const validation = await validateBundleDeal(payload).unwrap();
      if (validation.valid === false) {
        message.error(validation.message || "Bundle deal payload is invalid");
        return;
      }
      if (validation.active === false && validation.hasExpiry) {
        message.warning(
          "This deal will not be live yet (inactive date window). Saving anyway.",
        );
      }

      if (isCreate) {
        const res = await postBundleDeal(payload).unwrap();
        message.success(res.message || "Bundle deal created.");
      } else {
        const updatePayload: UpdateBundleRequest = {
          ...payload,
          updatedBy: "admin",
        };
        const res = await putBundleDeal(updatePayload).unwrap();
        message.success(res.message || "Bundle deal updated.");
      }
      props.onSuccess?.();
      props.onCancel();
    } catch (e: any) {
      message.error(e?.data?.message || e?.message || "Error saving bundle deal");
    }
  };

  return (
    <Modal
      title={
        isCreate
          ? "Create Bundle Deal"
          : internalMode === "edit"
            ? "Edit Bundle Deal"
            : "Bundle Deal Details"
      }
      open={props.showModal}
      onCancel={props.onCancel}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        disabled={isView}
      >
        <Card style={{ marginBottom: 16, background: "#fafafa" }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="promoCode"
                label="Promo Code"
                rules={[
                  { required: true, message: "Enter a promo code" },
                  {
                    pattern: /^[A-Za-z0-9_-]+$/,
                    message: "Alphanumeric, hyphens and underscores only",
                  },
                ]}
              >
                <Input
                  placeholder="e.g. MULTI2"
                  disabled={!isCreate}
                  onChange={(e) =>
                    form.setFieldsValue({
                      promoCode: e.target.value.toUpperCase().replace(/\s+/g, ""),
                    })
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: "Select status" }]}
              >
                <Select
                  options={[
                    { label: "ACTIVE", value: "ACTIVE" },
                    { label: "INACTIVE", value: "INACTIVE" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Internal Name">
                <Input placeholder="e.g. Multi-Buy Discount" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Save more when you buy more" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="middle">
            <Col span={12}>
              <Flex gap={8} align="end">
                <Form.Item
                  name="startAt"
                  label="Start At (SGT)"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <DatePicker
                    showTime
                    style={{ width: "100%" }}
                    disabled={noStartDate}
                  />
                </Form.Item>
                <Checkbox
                  checked={noStartDate}
                  onChange={(e) => {
                    setNoStartDate(e.target.checked);
                    if (e.target.checked) form.setFieldsValue({ startAt: null });
                  }}
                  style={{ marginBottom: 8 }}
                >
                  Immediate
                </Checkbox>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex gap={8} align="end">
                <Form.Item
                  name="endAt"
                  label="End At (SGT)"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <DatePicker
                    showTime
                    style={{ width: "100%" }}
                    disabled={noExpiry}
                  />
                </Form.Item>
                <Checkbox
                  checked={noExpiry}
                  onChange={(e) => {
                    setNoExpiry(e.target.checked);
                    if (e.target.checked) form.setFieldsValue({ endAt: null });
                  }}
                  style={{ marginBottom: 8 }}
                >
                  No Expiry
                </Checkbox>
              </Flex>
            </Col>
          </Row>
        </Card>

        <Card title="Tiers" style={{ marginBottom: 16, background: "#fafafa" }}>
          <Form.List name="tiers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Flex
                    key={key}
                    gap="middle"
                    align="end"
                    wrap="wrap"
                    style={{ marginBottom: 12 }}
                  >
                    <Text strong style={{ width: 20, marginBottom: 8 }}>
                      {index + 1}.
                    </Text>
                    <Form.Item
                      {...restField}
                      name={[name, "minQty"]}
                      label="Min Qty"
                      rules={[{ required: true, message: "Required" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={1} style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "discountType"]}
                      label="Discount Type"
                      rules={[{ required: true, message: "Required" }]}
                      style={{ marginBottom: 0, width: 160 }}
                    >
                      <Select
                        options={[
                          { label: "Percentage", value: "PERCENTAGE" },
                          { label: "Fixed Amount", value: "FIXED_AMOUNT" },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "discountValue"]}
                      label="Value"
                      rules={[
                        { required: true, message: "Required" },
                        {
                          validator: async (_, val) => {
                            if (val == null || val <= 0) {
                              return Promise.reject(
                                new Error("Must be greater than 0"),
                              );
                            }
                            const type = form.getFieldValue([
                              "tiers",
                              name,
                              "discountType",
                            ]);
                            if (type === "PERCENTAGE" && val > 100) {
                              return Promise.reject(
                                new Error("Percentage cannot exceed 100"),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0.01} style={{ width: 120 }} />
                    </Form.Item>
                    {fields.length > 1 && !isView && (
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginBottom: 4 }}
                      />
                    )}
                  </Flex>
                ))}
                {!isView && (
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        minQty: 1,
                        discountType: "PERCENTAGE",
                        discountValue: 10,
                      })
                    }
                    icon={<PlusOutlined />}
                    size="small"
                  >
                    Add Tier
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Card>

        <Card
          title="Eligible Products"
          style={{ marginBottom: 16, background: "#fafafa" }}
        >
          <Form.Item name="productSelection" style={{ marginBottom: 12 }}>
            <Radio.Group>
              <Radio value="all">All Products</Radio>
              <Radio value="selected">Specific Products</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.productSelection !== curr.productSelection
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("productSelection") === "selected" ? (
                <Form.Item
                  name="productIds"
                  label="Selected Products"
                  rules={[
                    { required: true, message: "Select at least one product" },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Search and select products"
                    options={productOptions}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Card>

        <Card
          title="Combinations"
          style={{ marginBottom: 16, background: "#fafafa" }}
        >
          <Flex gap={16} wrap="wrap">
            <Form.Item name="simplyPoints" valuePropName="checked" noStyle>
              <Checkbox>Combine with SIMPLY Points</Checkbox>
            </Form.Item>
            <Form.Item name="otherPromoCode" valuePropName="checked" noStyle>
              <Checkbox>Combine with other promo codes</Checkbox>
            </Form.Item>
            <Form.Item
              name="membershipBenefits"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Combine with membership benefits</Checkbox>
            </Form.Item>
          </Flex>
        </Card>

        <Flex gap="small" justify="flex-end">
          <Button onClick={props.onCancel}>Cancel</Button>
          {isView ? (
            <Button type="primary" onClick={() => setInternalMode("edit")}>
              Edit
            </Button>
          ) : (
            <Button type="primary" htmlType="submit" loading={isSaving}>
              Save
            </Button>
          )}
        </Flex>
      </Form>
    </Modal>
  );
}
